import React, { useState } from 'react'
import {
  TableCell,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
  IconButton,
} from '@resiliatech/birdsong'
import { Create, Delete, MoreVert } from '@mui/icons-material'
import { analytics } from 'Modules/Analytics'
import { team } from 'Modules/Analytics/trackActionInterface/Account/team'
import { CircularProgress } from '@mui/material'
import { RESEND_INVITE } from 'Apollo/Mutations/ResendInvite'
import { useMutation } from '@apollo/client'
import GET_NONPROFIT_ORG_CONFIG_QUERY from 'Apollo/Queries/GetNonprofitOrgConfig'
import { useAppState } from 'Context/AppContext/AppContext'
import GET_NONPROFIT_ORG_TEAM_QUERY from 'Apollo/Queries/GetNonprofitOrgTeam'
import { debounce } from 'lodash'
import { NonprofitOrgUser } from 'TypeDefs/Team'

type Props = {
  setShowBirdsongSnackbar: (value: React.SetStateAction<boolean>) => void
  setBirdsongSnackbarMessage: (value: React.SetStateAction<string>) => void
  nonprofitOrgUser: NonprofitOrgUser
  setDeletedUser: (
    value: React.SetStateAction<{
      id: number
      email: string
      name?: string
    } | null>
  ) => void
}

export const ActionButtonCell = ({
  setShowBirdsongSnackbar,
  setBirdsongSnackbarMessage,
  nonprofitOrgUser,
  setDeletedUser,
}: Props) => {
  // As any role, I have the edit icon for myself that takes me to Your Account settings.
  // no one can delete themselves
  // admin/owner has the ... for all invited admins and members.
  // admin/owner has the trash icon for all joined admins and members

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)
  const {
    nonprofitOrgConfig: {
      orgId: nonprofitOrgId,
      platformPlan,
      role,
      nonprofitOrgRole,
    },
    userConfig: { email: currentUserEmail, id: currentUserId, permissions },
    funderOrgConfig: { orgId: funderOrgId },
  } = useAppState()

  const isSelf = nonprofitOrgUser.user.id === currentUserId
  const isActive =
    !!nonprofitOrgUser.user.firstName && !!nonprofitOrgUser.user.lastName
  const dispatchEditProfileClicked = analytics.trackActionCreator(
    team.Edit_Profile_Clicked
  )
  const dispatchResendInviteSubmitted = analytics.trackActionCreator(
    team.Resend_Invite_Submitted
  )
  const dispatchRemoveUserClicked = analytics.trackActionCreator(
    team.Remove_User_Clicked
  )

  const handleEditSelfClicked = () => {
    dispatchEditProfileClicked({
      userId: currentUserId,
      email: currentUserEmail,
      nonprofitOrgId,
      platformPlan,
      funderOrgId,
      role,
    })
  }

  // was lastInviteSentAt within the last 5 minutes
  const currentTime = new Date().getTime()
  const inviteTimeMillis = new Date(nonprofitOrgUser.lastInviteSentAt).getTime()
  const noResend = currentTime - inviteTimeMillis <= 300000
  // get minutes until next resend
  const minutesUntilResend = Math.ceil(
    (300000 - (currentTime - inviteTimeMillis)) / 60000
  )

  const [resendInvite, { loading: resendLoading }] = useMutation(
    RESEND_INVITE,
    {
      refetchQueries: [
        {
          query: GET_NONPROFIT_ORG_CONFIG_QUERY,
          variables: { nonprofitOrgId },
        },
        {
          query: GET_NONPROFIT_ORG_TEAM_QUERY,
          variables: {
            nonprofitOrgId,
            planName: platformPlan,
            featureName: 'Team',
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        setShowBirdsongSnackbar(true)
        setBirdsongSnackbarMessage('Invitation successfully resent!')
        dispatchResendInviteSubmitted({
          accountType: nonprofitOrgUser.nonprofitOrgRole || 'Unknown', // user being invited
          email: currentUserEmail, // user submitting change
          funderOrgId,
          invitedTeamMember: nonprofitOrgUser.user.email, // email address
          invitedTeamMemberDate: new Date(),
          userId: currentUserEmail, // user submitting change
          inviterAccountType: nonprofitOrgRole,
          nonprofitOrgId,
          platformPlan,
          role, // user submitting change
        })
      },
      onError: () => {
        setShowBirdsongSnackbar(true)
        setBirdsongSnackbarMessage(
          'Previous invitation was sent less than 5 minutes ago'
        )
      },
    }
  )

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleDelete = async () => {
    dispatchRemoveUserClicked({
      userId: nonprofitOrgUser.user.id,
      email: nonprofitOrgUser.user.email,
      nonprofitOrgId,
      platformPlan: platformPlan as string,
      funderOrgId,
      role: nonprofitOrgUser.role,
    })
    handleMenuClose()
    const name =
      nonprofitOrgUser.user.firstName && nonprofitOrgUser.user.lastName
        ? `${nonprofitOrgUser.user.firstName} ${nonprofitOrgUser.user.lastName}`
        : ''
    setDeletedUser({
      id: nonprofitOrgUser.user.id,
      email: nonprofitOrgUser.user.email,
      name,
    })
  }

  const handleResendInvite = async () => {
    const userId = nonprofitOrgUser.user.id
    await resendInvite({
      variables: { currentUserEmail, userId, nonprofitOrgId },
    })
    handleMenuClose()
  }
  const debouncedResendInvite = debounce(handleResendInvite, 2500, {
    leading: true,
    trailing: false,
  })

  if (isSelf) {
    return (
      <TableCell>
        <IconButton
          color='primary'
          aria-label='edit'
          component='a'
          href='/settings/account'
          onClick={handleEditSelfClicked}
        >
          <Create />
        </IconButton>
      </TableCell>
    )
  }

  if (isActive && permissions.includes('team:update')) {
    return (
      <TableCell>
        <IconButton onClick={handleDelete} color='error' aria-label='delete'>
          <Delete />
        </IconButton>
      </TableCell>
    )
  }

  if (!isActive && permissions.includes('team:update')) {
    return (
      <>
        <TableCell>
          <IconButton
            onClick={handleMenuClick}
            color='primary'
            disabled={resendLoading}
            aria-label='more'
            data-testid='more'
          >
            {resendLoading ? (
              <CircularProgress
                size={20}
                style={{ color: 'rgba(0, 0, 0, 0.38)' }}
              />
            ) : (
              <MoreVert />
            )}
          </IconButton>
        </TableCell>
        <Menu open={menuOpen} anchorEl={anchorEl} onClose={handleMenuClose}>
          <Tooltip
            title={
              noResend
                ? `You can resend this invite in ${minutesUntilResend} minute${
                    minutesUntilResend === 1 ? '' : 's'
                  }`
                : ''
            }
          >
            <MenuItem
              onClick={() => {
                handleMenuClose()
                debouncedResendInvite()
              }}
              disabled={noResend}
            >
              <ListItemText>Resend invite</ListItemText>
            </MenuItem>
          </Tooltip>
          <MenuItem onClick={handleDelete}>
            <ListItemText sx={{ color: '#871919' }}>
              Remove team member
            </ListItemText>
          </MenuItem>
        </Menu>
      </>
    )
  }

  return <TableCell />
}
